import { useEffect, useState, useRef, useCallback } from "react";
import { LensPlatformClient, LensSDKException } from "lens-platform-sdk";
import { useLensPlatformClient } from "./useLensPlatformClient";

export type UserEmails = Awaited<ReturnType<LensPlatformClient["user"]["getEmails"]>>;

type ValueOf<T> = T[keyof T];

export type EmailStatus = ValueOf<UserEmails>;

/**
 * Get user's emails
 */
export const useGetEmails = () => {
  const lensPlatformClient = useLensPlatformClient();

  const [emails, setEmails] = useState<UserEmails>({});

  const [error, setError] = useState<LensSDKException | null>(null);
  const [loading, setLoading] = useState(false);

  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const getEmails = useCallback(async () => {
    try {
      if (mounted.current) {
        setLoading(true);
      }
      const response = await lensPlatformClient.user.getEmails();

      if (mounted.current) {
        setEmails(response);
        setError(null);
      }
    } catch (exception) {
      if (mounted.current && exception instanceof LensSDKException) {
        setError(exception);
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [lensPlatformClient.user]);

  useEffect(() => {
    // Load emails on mount
    getEmails();
  }, [getEmails]);

  return {
    loading,
    error,
    emails,
    reload: getEmails,
  };
};
